import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage, SSEEvent, ToolCall } from "../types/chat";
import { useUIStore } from "../store/uiStore";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

interface UseChatOpts {
  cellId: string;
  tickId: string;
  cellContext?: Record<string, unknown>;
}

export function useChat({ cellId, tickId, cellContext }: UseChatOpts) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Use selector pattern so action references are stable; including the
  // setter in a useEffect dep array can otherwise re-fire the reset effect
  // when applyEvent updates chatSessionId mid-stream — wiping the in-flight
  // message and aborting the fetch.
  const chatSessionId = useUIStore((s) => s.chatSessionId);
  const setChatSessionId = useUIStore((s) => s.setChatSessionId);

  // Reset on cell change ONLY. Do NOT depend on setChatSessionId.
  useEffect(() => {
    setMessages([]);
    setIsStreaming(false);
    setChatSessionId(null);
    abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellId, tickId]);

  // Abort on unmount
  useEffect(() => () => {
    abortRef.current?.abort();
  }, []);

  const send = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming) return;

      const userMsg: ChatMessage = { role: "user", content: text };
      const assistantMsg: ChatMessage = { role: "assistant", content: "", toolCalls: [] };
      setMessages((m) => [...m, userMsg, assistantMsg]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;

      try {
        const resp = await fetch(`${API_BASE}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
          body: JSON.stringify({
            cell_id: cellId,
            tick_id: tickId,
            user_message: text,
            session_id: chatSessionId,
            cell_context: cellContext,
          }),
          signal: controller.signal,
        });

        if (!resp.ok || !resp.body) {
          throw new Error(`chat request failed: ${resp.status}`);
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let lastEventType: string | null = null;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE messages are separated by either \n\n or \r\n\r\n depending on
          // the server. sse-starlette uses CRLF, so strip \r before splitting
          // to handle both consistently.
          const normalized = buffer.replace(/\r\n/g, "\n");
          const blocks = normalized.split("\n\n");
          buffer = blocks.pop() ?? "";
          for (const block of blocks) {
            const lines = block.split("\n");
            let dataLine = "";
            for (const line of lines) {
              if (line.startsWith("event:")) lastEventType = line.slice(6).trim();
              if (line.startsWith("data:")) dataLine += line.slice(5).trim();
            }
            if (!dataLine) continue;
            try {
              const ev = JSON.parse(dataLine) as SSEEvent;
              // Debug: log every SSE event so we can spot drop-outs.
              // eslint-disable-next-line no-console
              console.debug("[chat] sse", (ev as { type: string }).type, ev);
              applyEvent(ev, setMessages, setChatSessionId);
              if ((ev as { type: string }).type === "done") {
                setIsStreaming(false);
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("[chat] parse fail", e, dataLine.slice(0, 200));
            }
            void lastEventType;
          }
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setMessages((m) => {
            const next = [...m];
            const last = next[next.length - 1];
            if (last && last.role === "assistant") {
              last.content += `\n\n[Error: ${(err as Error).message}]`;
            }
            return next;
          });
        }
      } finally {
        setIsStreaming(false);
      }
    },
    [cellId, tickId, cellContext, chatSessionId, isStreaming, setChatSessionId],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  return { messages, send, cancel, isStreaming };
}


function applyEvent(
  ev: SSEEvent,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setChatSessionId: (id: string | null) => void,
) {
  if ("type" in ev && ev.type === "session") {
    setChatSessionId(ev.session_id);
    return;
  }

  setMessages((prev) => {
    const next = [...prev];
    const last = next[next.length - 1];
    if (!last || last.role !== "assistant") return prev;

    const calls = last.toolCalls ?? [];
    const updated: ChatMessage = { ...last, toolCalls: [...calls] };

    switch ((ev as { type: string }).type) {
      case "tool_call": {
        const e = ev as Extract<SSEEvent, { type: "tool_call" }>;
        const newCall: ToolCall = {
          call_id: e.call_id,
          name: e.name,
          args: e.args,
          status: "pending",
          startedAt: Date.now(),
        };
        updated.toolCalls = [...(updated.toolCalls ?? []), newCall];
        break;
      }
      case "tool_result": {
        const e = ev as Extract<SSEEvent, { type: "tool_result" }>;
        const now = Date.now();
        updated.toolCalls = (updated.toolCalls ?? []).map((tc) =>
          tc.call_id === e.call_id
            ? { ...tc, status: "done" as const, result: e.result, endedAt: now }
            : tc,
        );
        break;
      }
      case "content": {
        const e = ev as Extract<SSEEvent, { type: "content" }>;
        updated.content += e.delta;
        break;
      }
      case "done":
        // no-op; isStreaming flipped externally
        break;
    }

    next[next.length - 1] = updated;
    return next;
  });
}
